
import {
  defineComponent,
  nextTick,
  onMounted,
  PropType,
  ref,
  toRef,
  watch,
} from "vue";
import { Form, Field, ErrorMessage } from "vee-validate";
import * as Yup from "yup";
import { UserAPI } from "@/services/api/User.service";
import { SiteApi } from "@/services/api/Site.service";
import { useI18n } from "vue-i18n/index";
import { getLookups } from "@/services/api/Lookup.service";
import { ISite, ISiteUser } from "@/interfaces/iSite";
import { useToast } from "vue-toastification";
import VZMultiSelect from "./VZMultiSelect.vue";

export default defineComponent({
  name: "add-site-user-modal",
  emit: ["submit", "cancel", "update:modelValue"],
  props: {
    data: { type: Object as PropType<ISite>, required: true },
    tableData: { type: Array as any, required: true },
    modelValue: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    Form,
    Field,
    ErrorMessage,
    VZMultiSelect,
  },
  setup(props, { emit }) {
    const { t, te } = useI18n();
    const translate = (key: string) => {
      if (te(key)) {
        return t(key);
      } else {
        return key.split(".").join(" ");
      }
    };

    const toast = useToast();
    const userAccountId = ref<any>();
    const siteId = ref<number>();
    const userRoleId = ref<any>();
    let userOptions = ref<any>([]);
    let roleOptions = ref<any>([]);
    const dataProp = toRef(props, "data");
    const modelValueProp = toRef(props, "modelValue");
    const dataOfTable = toRef(props, "tableData");
    const hideModalRef = ref<null | HTMLElement>();
    const formRef = ref<null | HTMLFormElement>(null);
    let siteUserTargetData = ref<any>({});
    const userAssignedMsg = t("pages.site.userAssignedMsg");
    let siteUserValidationSchema = ref(
      Yup.object({
        userRoleId: Yup.string().nullable(),
        // .required(t("pages.site.role") + " " + t("common.isRequired")),
        userAccountId: Yup.array()
          .nullable()
          .required(t("pages.site.user") + " " + t("common.isRequired")),
      })
    );
    const loading = ref<boolean>(false);

    onMounted(async () => {
      // getData();
    });
    const getData = async () => {
      await getLookups(`security/filter/USERS`)
        .then((data: any) => {
          userOptions.value = [];
          data.map((obj: any) => {
            userOptions.value.push({
              label: obj.text,
              value: obj.value,
            });
          });
        })
        .catch(() => {
          return [];
        });
      await getLookups(`security/filter/ROLES`).then((res) => {
        roleOptions.value = [];
        res.map((data) => {
          roleOptions.value.push({
            label: data.text,
            value: data.value,
          });
        });
      });
      const userAccountIds = dataOfTable.value.map((obj) =>
        obj.userAccountId.toString()
      );
      const filteredArray = userOptions.value.filter(
        (obj) => !userAccountIds.includes(obj.value)
      );
      userOptions.value = filteredArray;
    };

    // const loadRoleByUserId = async ($event) => {
    //   await UserAPI.getInstance()
    //     .getActiveRoles($event)
    //     .then((data: any) => {
    //       roleOptions.value = [];
    //       data.content.map((obj: any) => {
    //         roleOptions.value.push({
    //           label: obj.name,
    //           value: obj.webId,
    //         });
    //       });
    //     })
    //     .catch((err) => {
    //       //
    //     });
    // };

    const discard = () => {
      emit("update:modelValue", false);
      siteUserTargetData.value = {};
      formRef?.value?.resetForm();
    };

    const submit = async (reqData) => {
      let hasError = false;

      // Clear existing errors
      formRef.value?.setFieldError("userAccountId", "");
      formRef.value?.setFieldError("userRoleId", "");

      // Check if both fields are empty
      if (
        (!siteUserTargetData.value.userAccountId ||
          siteUserTargetData.value.userAccountId.length === 0) &&
        !siteUserTargetData.value.userRoleId
      ) {
        formRef.value?.setFieldError(
          "userAccountId",
          t("pages.site.user") + " " + t("common.isRequired")
        );
        formRef.value?.setFieldError(
          "userRoleId",
          t("pages.site.role") + " " + t("common.isRequired")
        );
        hasError = true;
      } else {
        // Check if userAccountId is missing
        if (
          !siteUserTargetData.value.userAccountId ||
          siteUserTargetData.value.userAccountId.length === 0
        ) {
          formRef.value?.setFieldError(
            "userAccountId",
            t("pages.site.user") + " " + t("common.isRequired")
          );
          hasError = true;
        }

        // Check if userRoleId is missing
        if (!siteUserTargetData.value.userRoleId) {
          formRef.value?.setFieldError(
            "userRoleId",
            t("pages.site.role") + " " + t("common.isRequired")
          );
          hasError = true;
        }
      }

      // If there are validation errors, stop the form submission
      if (hasError) {
        return false;
      }

      // Proceed with form submission if no validation errors
      let reqDataArray: {
        siteId: number;
        userAccountId: number;
        roleId: number;
      }[] = [];

      siteUserTargetData.value.userAccountId.forEach((userAccountId: any) => {
        let reqDataItem: {
          siteId: number;
          userAccountId: number;
          roleId: number;
        } = {
          siteId: dataProp.value.webId,
          userAccountId: Number(userAccountId),
          roleId: Number(siteUserTargetData.value.userRoleId),
        };
        reqDataArray.push(reqDataItem);
      });

      await SiteApi.getInstance()
        .addMultiSiteUser(reqDataArray)
        .then(() => {
          toast.success(userAssignedMsg);
        })
        .catch(() => {
          //
        })
        .finally(() => {
          discard();
          emit("submit");
          // getData();
        });
    };

    // watch
    watch(modelValueProp, (val) => {
      if (val) {
        siteUserTargetData.value = { userAccountId: [], userRoleId: null };
        formRef?.value?.resetForm();
      }
    });

    return {
      loading,
      hideModalRef,
      submit,
      discard,
      translate,
      userAccountId,
      siteId,
      userRoleId,
      t,
      siteUserValidationSchema,
      formRef,
      userOptions,
      roleOptions,
      siteUserTargetData,
      modelValueProp,
      dataOfTable,
      getData,
    };
  },
});
